import { useCADashboard, getWidgets as getCAWidgets, CA_DASHBOARD, useCurrentDay } from './useCADashboard';
import {DashboardWidget} from './DashboardWidget';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { annotateWidgetsWithPermission } from './permissionUtil';
import useUserSettingStore from './UserSettingStore';
import userProfileStore from '../core/UserProfileStore';
import UserCreationFilter from './filters/UserCreationFilter';


const styles = {
  gridWrapper: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '16px',
    alignItems: 'stretch',
  },
  cardWidget: {
    display: 'flex',
    gap:'20px',
    flexDirection: 'column' as const,
  },
  emptyState: {
    padding: '24px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    backgroundColor: '#ffffff',
    color: '#4b5563',
    fontSize: '14px',
  }
};


export const useDashboardRegistry = () => {
  const { t } = useTranslation();
  const { dashboardAccessSetting, userSetting } = useUserSettingStore();
  const { permissions, enterpriseId } = userProfileStore();

  const dashboardFilter = userSetting?.dashboard?.find((d: any) => d.id === CA_DASHBOARD.DASHBOARD_ID)?.filter || {};
  const today = useCurrentDay();

  const caPermissionWidgets = useMemo(() => {
    if (!enterpriseId) return [];
    return annotateWidgetsWithPermission(
      getCAWidgets(t, enterpriseId, dashboardFilter),
      CA_DASHBOARD.DOMAIN_ID,
      dashboardAccessSetting,
      permissions
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dashboardAccessSetting,
    permissions,
    t,
    enterpriseId,
    dashboardFilter.startDate,
    dashboardFilter.endDate,
    dashboardFilter.dateRangePreset,
    today,
  ]);

  const caDashboard = useCADashboard({
    enabled: Array.isArray(caPermissionWidgets) && caPermissionWidgets.some((w) => w.hasPermission),
    filteredWidgets: caPermissionWidgets,
  });

  const dashboards = [
    { id: CA_DASHBOARD.DASHBOARD_ID, ...caDashboard },
  ] as any;

  return {
    caDashboard,
    getWidgetState: (dashboardId:string, widgetId:string) => {
      const dashboard = dashboards.find((d:any) => d.id === dashboardId);
      const widget = dashboard.widgets?.find((w:any) => w.id === widgetId);

      return {
        isLoading: dashboard.isLoading,
        error: typeof dashboard.getWidgetError === 'function'
          ? dashboard.getWidgetError(widget)
          : dashboard.error,
        widgetData: widget ? dashboard.getWidgetData(widget) : null,
        widget: widget,
        filter: dashboard.filter,
      };
    }
  };
  
};

export const CADashboard = () => {
  const { getWidgetState, caDashboard } = useDashboardRegistry();
  const { t } = useTranslation();
  const visibleWidgets = caDashboard.widgets || [];
  const shouldShowEmptyState = Array.isArray(caDashboard.widgets) && !caDashboard.isLoading && visibleWidgets.length === 0;

  return (
       <div >
        {shouldShowEmptyState && (
          <div style={styles.emptyState}>
            {t('dashboard.insufficientPermissions', 'This chart is unavailable as you do not have required permissions.')}
          </div>
        )}
        <div style={styles.gridWrapper}>
          {visibleWidgets.map((widget: any) => {
            const state = getWidgetState(CA_DASHBOARD.DASHBOARD_ID, widget.id);
            const isUsersCreatedWidget = widget.id === CA_DASHBOARD.WIDGET_USERS_CREATED_ID;
            return (
              <div key={widget.id} style={styles.cardWidget}>
                <DashboardWidget
                  state={state}
                  filterComponent={isUsersCreatedWidget ? UserCreationFilter : undefined}
                  loaderCount={widget.id === CA_DASHBOARD.WIDGET_ACC_DISABLED_ID ||
                    widget.id === CA_DASHBOARD.WIDGET_MFA_ENABLED_ID ||
                    widget.id === CA_DASHBOARD.WIDGET_DMS_ASSGN_ID ? 3 : 2}
                />
              </div>
            );
          })}
        </div>
    </div>
  );
};
